import React, { useEffect, useState, Fragment, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bus, Users, GraduationCap, Heart, CreditCard, CalendarClock, ShieldCheck, FileText, CheckCircle2, ArrowRight, ArrowLeft, ChevronDown, ChevronRight, Sparkles, Building2, UserCircle, Award, Lock, Receipt, Phone, Mail, Info, Quote, MapPin, Send, CircleDollarSign, Loader2, Crown } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// ---------- Shared visual primitives ----------
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-7325" data-mp="1vtvqucg8caa9">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
  align?: 'center' | 'left';
}) => <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`} data-id="element-7326" data-mp="3tjfwm9e8ko05">
    <Eyebrow data-id="element-7327" data-mp="3ewcxjya9m7ap">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-7328" data-mp="3ev1s7u168rhh">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-7329" data-mp="35u3q5haamu1j">{highlight}</span>
        </>}
    </h2>
    <div className={`w-16 h-px bg-amber-400/30 mb-6 ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-7330" data-mp="3av14tjw3h5ya" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-7331" data-mp="2vya519iids85">
        {intro}
      </p>}
  </div>;
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-amber-400/40 pointer-events-none" data-id="element-7332" data-mp="u6f8600wvs9z" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-amber-400/40 pointer-events-none" data-id="element-7333" data-mp="z3x7qzt1gv5b" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-amber-400/40 pointer-events-none" data-id="element-7334" data-mp="xl6wzrv8h9n7" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-amber-400/40 pointer-events-none" data-id="element-7335" data-mp="np3xyuvohr4h" />
  </>;
// ---------- Types ----------
type SponsorshipType = 'full' | 'shared' | 'flexible' | '';
type PaymentPlan = 'full' | 'installments' | '';
type RecognitionPref = 'anonymous' | 'public' | 'family' | 'organization' | 'discuss' | '';
type CheckoutStep = 'type' | 'payment' | 'details' | 'review' | 'processing' | 'confirmed';
// =====================================================================
// HERO
// =====================================================================
const Hero = ({
  scrollToCheckout,
  scrollToOptions,
  scrollToTax
}: {
  scrollToCheckout: () => void;
  scrollToOptions: () => void;
  scrollToTax: () => void;
}) => <section className="relative w-full min-h-[90vh] bg-black flex items-center overflow-hidden pt-20" data-id="element-7336" data-mp="3d0adq9ei5j8j">
    <div className="absolute inset-0" data-id="element-7337" data-mp="3hzdl40hyg1jo">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,175,55,0.15),_transparent_60%)] pointer-events-none" data-id="element-7338" data-mp="3i0ev01n8ihzt" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(139,0,0,0.1),_transparent_60%)] pointer-events-none" data-id="element-7339" data-mp="3m3bkmcb6zed9" />
    </div>

    <div className="relative z-10 container mx-auto px-4 py-16" data-id="element-7340" data-mp="3vy3jdjgig79m">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center max-w-7xl mx-auto" data-id="element-7341" data-mp="4slkeazil7u3">
        {/* Left */}
        <div className="lg:col-span-7" data-id="element-7342" data-mp="9jbozm0496nq">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/30 mb-7 bg-black/40 backdrop-blur-sm" data-id="element-7343" data-mp="9kdcipybjlmc">
            <Bus className="w-3.5 h-3.5 text-amber-400" data-id="element-7344" data-mp="9ut852b8eaqi" />
            <span className="text-amber-400 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-7345" data-mp="4yb2m082u8d4">
              Sponsor a Bus · Student Access Program
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="font-serif text-5xl sm:text-6xl md:text-7xl text-white leading-[1.02] tracking-tight mb-6" data-id="element-7346" data-mp="en2uk8d50so1">
            Sponsor a <span className="text-amber-400" data-id="element-7347" data-mp="3s0nzv8ctha52">Bus</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.25
        }} className="text-xl sm:text-2xl text-white/75 font-light leading-relaxed mb-5 max-w-2xl" data-id="element-7348" data-mp="1ca1s0lbnm4mz">
            Help bring students into Life of the Sikh Kingdoms through sponsored
            access and school transportation support.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.35
        }} className="text-base text-white/55 font-light leading-relaxed mb-9 max-w-xl" data-id="element-7349" data-mp="19y9apea8kt7c">
            Your sponsorship helps middle school and high school students
            experience Sikh history in a vivid, memorable, and meaningful way.
            Sponsors can fund a full bus, contribute toward a shared
            sponsorship, and choose full payment or installment options.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.45
        }} className="flex flex-col sm:flex-row items-start gap-3" data-id="element-7350" data-mp="2r607qksh43nx">
            <button onClick={scrollToCheckout} className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-7351" data-mp="2ca0pwifkap5l">
              <Bus className="w-4 h-4" data-id="element-7352" data-mp="1yf83r1562aq1" />
              Sponsor a Bus
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-7353" data-mp="1tgwtwqevg07t" />
            </button>
            <button onClick={scrollToOptions} className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-7354" data-mp="1ojwwt50027su">
              View Sponsorship Options
            </button>
          </motion.div>

          <motion.button onClick={scrollToTax} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.7,
          delay: 0.7
        }} className="mt-6 inline-flex items-center gap-2 text-white/45 hover:text-amber-400 text-xs tracking-wide transition-colors group" data-id="element-7355" data-mp="1tbyz6gt1gaxn">
            <Receipt className="w-3 h-3" data-id="element-7356" data-mp="cy8pmuj5gugm" />
            How Tax Receipts Work
            <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" data-id="element-7357" data-mp="cw4nah0wg3ig" />
          </motion.button>
        </div>

        {/* Right — impact callout */}
        <motion.div initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.9,
        delay: 0.4
      }} className="lg:col-span-5" data-id="element-7358" data-mp="1w4agwtnuwngd">
          <div className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-8" data-id="element-7359" data-mp="211aeeg6n4hvv">
            <CornerOrnaments data-id="element-7360" data-mp="2f0a13ialempe" />
            <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-7361" data-mp="2b1kqfxgxvj9g" />
            <Eyebrow data-id="element-7362" data-mp="2or4q2uf840ej">One Sponsored Bus</Eyebrow>
            <p className="font-serif text-6xl text-amber-400 mt-4 leading-none" data-id="element-7363" data-mp="2jtcyy2mekibe">
              ~50
            </p>
            <p className="text-white/55 text-sm mt-1 font-light" data-id="element-7364" data-mp="2yq3zif7tc02l">
              students reached per bus
            </p>

            <div className="mt-7 space-y-3 text-sm" data-id="element-7365" data-mp="2tsllwbiuowo9">
              {['Round-trip transportation', 'Student access tickets', 'School participation support', 'Educator coordination'].map(item => <div key={item} className="flex items-center gap-3" data-id="element-7366" data-mp="33foa49gu43oh">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0" data-id="element-7367" data-mp="38e98028m6b9r" />
                  <span className="text-white/75 font-light" data-id="element-7368" data-mp="38cep0tgwkt8b">{item}</span>
                </div>)}
            </div>

            <div className="mt-7 pt-6 border-t border-amber-400/15" data-id="element-7369" data-mp="2yg1w3xt6mjem">
              <p className="text-white/40 text-xs font-light leading-relaxed flex items-start gap-2" data-id="element-7370" data-mp="2yi67f0teukox">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" data-id="element-7371" data-mp="2kmkbe9orf0n0" />
                Tax receipts, where applicable, are issued through
                <strong className="text-white/65 font-normal mx-1" data-id="element-7372" data-mp="2aq7ktkoih2wh">
                  Sikh Heritage Museum of Canada
                </strong>
                .
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// WHAT IT FUNDS
// =====================================================================
const WhatItFunds = () => {
  const items = [{
    icon: Bus,
    title: 'Student Transportation',
    body: 'Round-trip charter buses from schools to the experience and back.'
  }, {
    icon: GraduationCap,
    title: 'Access Tickets',
    body: 'Full immersive experience tickets covered for each student on the bus.'
  }, {
    icon: Building2,
    title: 'School Participation',
    body: 'Administrative coordination and educator support for participating schools.'
  }, {
    icon: Users,
    title: 'Middle &amp; High School Groups',
    body: 'Designed for grades 6–12. Reaches students who might not otherwise attend.'
  }];
  return <section className="relative py-24 md:py-28 bg-black border-t border-amber-400/10" data-id="element-7373" data-mp="1owbmti0vfyd3">
      <div className="container mx-auto px-4" data-id="element-7374" data-mp="39juozkz9r3ki">
        <SectionHeading eyebrow="What Sponsorship Funds" title="A Real Bus, A Real" highlight="Group of Students" intro="Each sponsored bus supports approximately 50 students from a partner school — transportation, tickets, and on-the-day support." data-id="element-7375" data-mp="34m2oi2kk694p" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14 max-w-6xl mx-auto" data-id="element-7376" data-mp="2qma40ozmkp56">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="relative border border-amber-400/15 hover:border-amber-400/45 transition-colors bg-gradient-to-b from-amber-950/10 to-transparent p-6" data-id="element-7377" data-mp="2lo8gnrv623nw" data-mpi={i}>
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-7378" data-mp="38nnz01jqu5a5" />
              <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-5" data-id="element-7379" data-mp="3dlpde9685gfa">
                <it.icon className="w-4 h-4 text-amber-400" data-id="element-7380" data-mp="2kz4qbkws7qhj" />
              </div>
              <h3 className="font-serif text-lg text-white leading-tight mb-2.5" dangerouslySetInnerHTML={{
            __html: it.title
          }} data-id="element-7381" data-mp="2kx1qya9qol05" />
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-7382" data-mp="2txzxaz7nh4lw">
                {it.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// SPONSORSHIP OPTIONS
// =====================================================================
const SponsorshipOptions = ({
  onChoose
}: {
  onChoose: (t: SponsorshipType) => void;
}) => {
  const options = [{
    id: 'full' as const,
    icon: Bus,
    tag: 'Full Sponsorship',
    title: 'Full Bus Sponsor',
    amount: '$5,000',
    blurb: 'Fund an entire bus and help bring a full student group to the experience.',
    supports: ['~50 students transported', 'Full ticket access included', 'Acknowledgment as a Full Bus Sponsor'],
    featured: true
  }, {
    id: 'shared' as const,
    icon: Users,
    tag: 'Shared',
    title: 'Shared Bus Sponsor',
    amount: '$1,000+',
    blurb: 'Contribute toward a bus together with other sponsors.',
    supports: ['Pooled with other contributors', 'Bus completion tracking', 'Shared sponsor recognition']
  }, {
    id: 'flexible' as const,
    icon: Heart,
    tag: 'Flexible',
    title: 'Flexible Supporter',
    amount: 'Choose Amount',
    blurb: 'Choose your own contribution amount toward student transportation and access.',
    supports: ['Any amount from $100', 'Direct student access support', 'Optional acknowledgment']
  }];
  return <section id="sponsorship-options" className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10 scroll-mt-20" data-id="element-7383" data-mp="2nc8taue6ygkh">
      <div className="container mx-auto px-4" data-id="element-7384" data-mp="2dfnjzwv52jau">
        <SectionHeading eyebrow="Sponsorship Options" title="Choose How You" highlight="Sponsor" intro="Three structured ways to support student access. Each is real, transparent, and operationally clear." data-id="element-7385" data-mp="2dgfawudsjsus" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-14 max-w-6xl mx-auto" data-id="element-7386" data-mp="3tu5em0100f3w">
          {options.map((opt, i) => <motion.div key={opt.id} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className={`relative border bg-gradient-to-b from-amber-950/15 to-black p-7 flex flex-col transition-colors ${opt.featured ? 'border-amber-400/50 hover:border-amber-400 shadow-[0_0_30px_rgba(212,175,55,0.08)]' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7387" data-mp="2mjh6p4vh8vh" data-mpi={i}>
              <span className={`absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent ${opt.featured ? 'via-amber-400' : 'via-amber-400/50'} to-transparent`} data-id="element-7388" data-mp="2fszm97t5qtyl" />
              {opt.featured && <span className="absolute -top-3 right-6 text-[9px] tracking-[0.3em] uppercase text-black bg-amber-400 px-2 py-1 font-bold" data-id="element-7389" data-mp="gh6imsbv6qcw">
                  Most Impact
                </span>}
              <div className="flex items-center justify-between mb-5" data-id="element-7390" data-mp="1a6nra5c35a3m">
                <div className="w-11 h-11 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-7391" data-mp="1a906wbudf8di">
                  <opt.icon className="w-5 h-5 text-amber-400" strokeWidth={1.25} data-id="element-7392" data-mp="1k53j6aixsne6" />
                </div>
                <span className="text-[9px] tracking-[0.3em] uppercase text-amber-400/70 font-light" data-id="element-7393" data-mp="1p3ojsb23p935">
                  {opt.tag}
                </span>
              </div>
              <h3 className="font-serif text-2xl text-white mb-2 leading-tight" data-id="element-7394" data-mp="1xw8ej0f9utip">
                {opt.title}
              </h3>
              <p className="font-serif text-3xl text-amber-400 mb-1 leading-tight" data-id="element-7395" data-mp="22v36arv7vsey">
                {opt.amount}
              </p>
              <p className="text-white/45 text-[10px] tracking-[0.25em] uppercase mb-5 font-light" data-id="element-7396" data-mp="27suxtkry9cim">
                {opt.id === 'flexible' ? 'Suggested $100+' : 'Suggested amount'}
              </p>
              <p className="text-white/65 text-sm font-light leading-relaxed mb-5" data-id="element-7397" data-mp="142pslxlwwa3">
                {opt.blurb}
              </p>
              <ul className="space-y-2.5 mb-7 flex-1" data-id="element-7398" data-mp="3ncu3msnxy9xc">
                {opt.supports.map(s => <li key={s} className="flex items-start gap-2.5" data-id="element-7399" data-mp="3ifc57w7jvd3l">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" data-id="element-7400" data-mp="jwx37bfl0gc7" />
                    <span className="text-white/70 text-sm font-light leading-relaxed" data-id="element-7401" data-mp="jyrcfshhqk0g">
                      {s}
                    </span>
                  </li>)}
              </ul>
              <button onClick={() => onChoose(opt.id)} className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors ${opt.featured ? 'bg-amber-400 text-black hover:bg-amber-300 shadow-[0_0_25px_rgba(212,175,55,0.25)]' : 'border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 hover:border-amber-400'}`} data-id="element-7402" data-mp="a4r2ndb9kqle">
                Sponsor This
                <ArrowRight className="w-3 h-3" data-id="element-7403" data-mp="b1ejazlsmfpj" />
              </button>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// PAYMENT OPTIONS EXPLAINER
// =====================================================================
const PaymentOptions = () => <section className="relative py-24 md:py-28 bg-black border-t border-amber-400/10" data-id="element-7404" data-mp="tz7ibqtaq5hn">
    <div className="container mx-auto px-4" data-id="element-7405" data-mp="fz6imeorp43r">
      <SectionHeading eyebrow="Payment Flexibility" title="Pay in Full or in" highlight="Installments" intro="Sponsors can complete their support in one payment or choose a structured installment plan through Stripe." data-id="element-7406" data-mp="fy4xtihy938u" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto mt-14" data-id="element-7407" data-mp="35nyo374petkw">
        {[{
        icon: CreditCard,
        tag: 'Option 1',
        title: 'One-Time Payment',
        body: 'Complete your sponsorship in full today through Stripe checkout. Secure, instant, with email confirmation.',
        chip: 'Processed instantly'
      }, {
        icon: CalendarClock,
        tag: 'Option 2',
        title: 'Installment Plan',
        body: 'Spread your sponsorship over scheduled payments while securing your commitment now. Installment sponsors still complete a formal commitment flow.',
        chip: 'Stripe scheduled payments'
      }].map((p, i) => <motion.div key={p.title} initial={{
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: i * 0.08
      }} className="relative border border-amber-400/20 hover:border-amber-400/50 transition-colors bg-gradient-to-b from-amber-950/15 to-black p-7" data-id="element-7408" data-mp="2o7ul99es7kof" data-mpi={i}>
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-7409" data-mp="wx2fntslg6pu" />
            <div className="flex items-center justify-between mb-5" data-id="element-7410" data-mp="s4hqx6ojojt6">
              <div className="w-12 h-12 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-7411" data-mp="15wpi8ix27ikh">
                <p.icon className="w-5 h-5 text-amber-400" strokeWidth={1.25} data-id="element-7412" data-mp="11v3ws96glvyo" />
              </div>
              <span className="text-[9px] tracking-[0.3em] uppercase text-amber-400/70 font-light" data-id="element-7413" data-mp="1fuc1fhle5wa9">
                {p.tag}
              </span>
            </div>
            <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-7414" data-mp="3hmrqzwu39ads">
              {p.title}
            </h3>
            <p className="text-white/65 text-sm font-light leading-relaxed mb-5" data-id="element-7415" data-mp="3coqczqbis196">
              {p.body}
            </p>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-amber-400/25 text-amber-400/85 text-[10px] tracking-[0.25em] uppercase" data-id="element-7416" data-mp="1s77fogttkukk">
              <Lock className="w-2.5 h-2.5" data-id="element-7417" data-mp="1igcsna8r1qy2" />
              {p.chip}
            </div>
          </motion.div>)}
      </div>
    </div>
  </section>;
// =====================================================================
// TAX RECEIPT EXPLAINER
// =====================================================================
const TaxReceipt = () => {
  const steps = [{
    n: '01',
    title: 'Indicate Receipt Request',
    body: 'Tell us during sponsorship if you would like a tax receipt issued.'
  }, {
    n: '02',
    title: 'Complete Required Details',
    body: 'Provide the legal name and address required for receipt issuance.'
  }, {
    n: '03',
    title: 'Processed by the Museum',
    body: 'Sikh Heritage Museum of Canada handles receipt processing and issuance.'
  }];
  return <section id="tax-receipt" className="relative py-24 md:py-28 bg-[#080808] border-t border-amber-400/10 scroll-mt-20" data-id="element-7418" data-mp="2xeqqamuafczo">
      <div className="container mx-auto px-4" data-id="element-7419" data-mp="2nezvsl5q8xzl">
        <SectionHeading eyebrow="Tax Receipt" title="How Tax Receipts" highlight="Are Issued" intro="Tax receipts, where applicable, are issued through Sikh Heritage Museum of Canada — not directly through Sikh Kingdoms." data-id="element-7420" data-mp="2nfrfoypyp7a1" />

        <div className="max-w-4xl mx-auto mt-14" data-id="element-7421" data-mp="3b42bvokpqirz">
          {/* 3 steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-id="element-7422" data-mp="36duaxv93s0ga">
            {steps.map((s, i) => <motion.div key={s.n} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="relative border border-amber-400/20 bg-gradient-to-b from-amber-950/10 to-transparent p-6" data-id="element-7423" data-mp="32cifva7gmn0p" data-mpi={i}>
                <span className="absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-7424" data-mp="1ukjv5h3fwny6" />
                <p className="font-serif text-amber-400/50 text-3xl leading-none mb-3" data-id="element-7425" data-mp="1zile820v8n9n">
                  {s.n}
                </p>
                <h4 className="font-serif text-lg text-white leading-tight mb-2" data-id="element-7426" data-mp="24gwp8g2wd3tg">
                  {s.title}
                </h4>
                <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-7427" data-mp="2db1j78dbuvd7">
                  {s.body}
                </p>
              </motion.div>)}
          </div>

          {/* Important callout */}
          <div className="border border-amber-400/25 bg-black/40 p-5 sm:p-6 flex items-start gap-4" data-id="element-7428" data-mp="2n8phg8a56l04">
            <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-7429" data-mp="29lqfgsmndw18" />
            <div className="flex-1" data-id="element-7430" data-mp="24nf6ek46fpk2">
              <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-bold mb-3" data-id="element-7431" data-mp="24mdu6cbfcx0r">
                Important
              </p>
              <ul className="space-y-1.5 text-white/65 text-xs sm:text-sm leading-relaxed font-light" data-id="element-7432" data-mp="2jee95eibzjvq">
                <li data-id="element-7433" data-mp="2efkw1n6yhuw3">
                  Sponsorship support may be eligible for a tax receipt where
                  applicable.
                </li>
                <li data-id="element-7434" data-mp="1lpmesdz0721z">
                  Tax receipts are issued by Sikh Heritage Museum of Canada.
                </li>
                <li data-id="element-7435" data-mp="20neryzh2ved0">
                  Sponsors who request a receipt must provide required
                  information.
                </li>
                <li data-id="element-7436" data-mp="3c0z0wg2pxxzt">
                  Receipt timing and issuance is handled through the museum
                  workflow.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// CHECKOUT FLOW — the heart of the page
// =====================================================================
type FormState = {
  sponsorshipType: SponsorshipType;
  amount: number;
  paymentPlan: PaymentPlan;
  installments: number;
  fullName: string;
  orgName: string;
  email: string;
  phone: string;
  taxReceipt: 'yes' | 'no' | '';
  legalName: string;
  mailingAddress: string;
  recognitionPref: RecognitionPref;
  notes: string;
  largerInterest: boolean;
};
const initialForm: FormState = {
  sponsorshipType: '',
  amount: 0,
  paymentPlan: '',
  installments: 3,
  fullName: '',
  orgName: '',
  email: '',
  phone: '',
  taxReceipt: '',
  legalName: '',
  mailingAddress: '',
  recognitionPref: '',
  notes: '',
  largerInterest: false
};
const Checkout = ({
  initialType,
  onConfirmed
}: {
  initialType: SponsorshipType;
  onConfirmed: (s: FormState) => void;
}) => {
  const [step, setStep] = useState<CheckoutStep>('type');
  const [form, setForm] = useState<FormState>(initialForm);
  // If parent passed a pre-selected type, jump forward
  useEffect(() => {
    if (initialType) {
      const defaultAmount = initialType === 'full' ? 5000 : initialType === 'shared' ? 1000 : 250;
      setForm(f => ({
        ...f,
        sponsorshipType: initialType,
        amount: defaultAmount
      }));
      setStep('payment');
    }
  }, [initialType]);
  const update = <K extends keyof FormState,>(k: K, v: FormState[K]) => setForm(prev => ({
    ...prev,
    [k]: v
  }));
  const stepIndex = (['type', 'payment', 'details', 'review', 'processing', 'confirmed'] as const).indexOf(step);
  const installmentAmount = form.amount / (form.installments || 1);
  // ---------- Validation per step ----------
  const canAdvance = () => {
    if (step === 'type') return form.sponsorshipType !== '' && form.amount > 0;
    if (step === 'payment') return form.paymentPlan !== '';
    if (step === 'details') {
      const base = form.fullName.length > 1 && /.+@.+\..+/.test(form.email) && form.phone.length > 4 && form.taxReceipt !== '' && form.recognitionPref !== '';
      if (form.taxReceipt === 'yes') {
        return base && form.legalName.length > 1 && form.mailingAddress.length > 5;
      }
      return base;
    }
    return true;
  };
  const handleNext = () => {
    if (!canAdvance()) return;
    if (step === 'review') {
      setStep('processing');
      setTimeout(() => {
        setStep('confirmed');
        onConfirmed(form);
      }, 1800);
      return;
    }
    const order: CheckoutStep[] = ['type', 'payment', 'details', 'review'];
    const i = order.indexOf(step);
    if (i >= 0 && i < order.length - 1) setStep(order[i + 1]);
  };
  const handleBack = () => {
    const order: CheckoutStep[] = ['type', 'payment', 'details', 'review'];
    const i = order.indexOf(step);
    if (i > 0) setStep(order[i - 1]);
  };
  const inputBase = 'w-full bg-black/60 border border-amber-400/25 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/70 transition-colors';
  return <section id="checkout" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 scroll-mt-20 overflow-hidden" data-id="element-7437" data-mp="9mziz3cp8757">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.06),_transparent_70%)] pointer-events-none" data-id="element-7438" data-mp="4m22cv7c7l5t" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-7439" data-mp="em2mukgy07bl">
        <SectionHeading eyebrow="Begin Your Sponsorship" title="Sponsor" highlight="Checkout" intro="A clear, four-step flow. Your information is processed securely through Stripe." data-id="element-7440" data-mp="ejge4hibflrs" />

        <div className="max-w-3xl mx-auto mt-14" data-id="element-7441" data-mp="12avj6i1h8h6q">
          {/* Stepper */}
          {step !== 'processing' && step !== 'confirmed' && <div className="mb-10 flex items-center justify-between gap-2" data-id="element-7442" data-mp="xdloamvbjdbi">
              {[{
            id: 'type',
            label: 'Sponsorship'
          }, {
            id: 'payment',
            label: 'Payment'
          }, {
            id: 'details',
            label: 'Details'
          }, {
            id: 'review',
            label: 'Review'
          }].map((s, i) => {
            const active = step === s.id;
            const past = stepIndex > i;
            return <Fragment key={s.id} data-id="element-7443" data-mp="o9onxkppzciz" data-mpi={i}>
                    <div className="flex flex-col items-center gap-2 flex-1" data-id="element-7444" data-mp="oapyzp6qjz0p">
                      <div className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-bold transition-colors ${active ? 'bg-amber-400 border-amber-400 text-black' : past ? 'bg-amber-400/20 border-amber-400/60 text-amber-400' : 'border-amber-400/20 text-amber-400/40'}`} data-id="element-7445" data-mp="t9azlppwgkpo">
                        {past ? <CheckCircle2 className="w-4 h-4" data-id="element-7446" data-mp="k4puesqg1hw7" /> : i + 1}
                      </div>
                      <p className={`text-[9px] tracking-[0.25em] uppercase ${active ? 'text-amber-400 font-bold' : 'text-white/40 font-light'}`} data-id="element-7447" data-mp="p3u19cpwb3jd">
                        {s.label}
                      </p>
                    </div>
                    {i < 3 && <div className={`flex-shrink-0 h-px w-6 sm:w-12 transition-colors -mt-6 ${past ? 'bg-amber-400/60' : 'bg-amber-400/15'}`} data-id="element-7448" data-mp="133bwoaxp5f1p" />}
                  </Fragment>;
          })}
            </div>}

          {/* Card */}
          <div className="relative border border-amber-400/30 bg-gradient-to-b from-amber-950/8 to-black p-7 sm:p-10" data-id="element-7449" data-mp="1gw1hwd5eifxr">
            <CornerOrnaments data-id="element-7450" data-mp="1lumi4cknkzm7" />
            <span className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-7451" data-mp="1lrqj0kx2k4w6" />

            <AnimatePresence mode="wait" data-id="element-7452" data-mp="31crhfb64e7gd">
              {/* STEP 1: TYPE */}
              {step === 'type' && <motion.div key="type" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} data-id="element-7453" data-mp="2xcgyyz155okm">
                  <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7454" data-mp="3bc073rtyczwx">
                    Choose Sponsorship Type
                  </h3>
                  <p className="text-white/55 text-sm font-light mb-7" data-id="element-7455" data-mp="cmjar43ixflh">
                    Pick how you would like to support student access.
                  </p>
                  <div className="space-y-3 mb-6" data-id="element-7456" data-mp="qrrxwl6berix">
                    {[{
                  id: 'full' as const,
                  label: 'Full Bus Sponsor',
                  sub: 'Fund an entire bus · ~50 students',
                  defaultAmt: 5000
                }, {
                  id: 'shared' as const,
                  label: 'Shared Bus Sponsor',
                  sub: 'Contribute toward a shared bus',
                  defaultAmt: 1000
                }, {
                  id: 'flexible' as const,
                  label: 'Flexible Supporter',
                  sub: 'Any amount from $100',
                  defaultAmt: 250
                }].map(opt => {
                  const active = form.sponsorshipType === opt.id;
                  return <button key={opt.id} type="button" onClick={() => {
                    update('sponsorshipType', opt.id);
                    update('amount', opt.defaultAmt);
                  }} className={`w-full flex items-center gap-4 border px-5 py-4 text-left transition-colors ${active ? 'border-amber-400/70 bg-amber-400/5' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7457" data-mp="3hwyazeiyjwp6">
                          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${active ? 'border-amber-400 bg-amber-400/30' : 'border-amber-400/40'}`} data-id="element-7458" data-mp="fycsna8r4da">
                            {active && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-7459" data-mp="2g8x7o1w0rafa" />}
                          </span>
                          <div className="flex-1" data-id="element-7460" data-mp="2bb5ihfmre2es">
                            <p className="font-serif text-white text-base leading-tight" data-id="element-7461" data-mp="2dmo5imw3mkix">
                              {opt.label}
                            </p>
                            <p className="text-white/45 text-xs font-light mt-0.5" data-id="element-7462" data-mp="2modxbttw7lh8">
                              {opt.sub}
                            </p>
                          </div>
                          <span className="text-amber-400 font-serif text-base shrink-0" data-id="element-7463" data-mp="1tzgriq6vbh5d">
                            ${opt.defaultAmt.toLocaleString()}
                          </span>
                        </button>;
                })}
                  </div>

                  {/* Custom amount */}
                  {form.sponsorshipType && <div className="border-t border-amber-400/15 pt-5" data-id="element-7464" data-mp="28po08e72plsv">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7465" data-mp="1b861sccvpaf2">
                        Adjust Amount (USD)
                      </label>
                      <div className="relative" data-id="element-7466" data-mp="18nhsmijmjveg">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-400 font-serif text-lg" data-id="element-7467" data-mp="18m6ouiq6innc">
                          $
                        </span>
                        <input type="number" min="100" step="50" value={form.amount || ''} onChange={e => update('amount', Number(e.target.value))} className={`${inputBase} pl-10 font-mono text-lg`} data-id="element-7468" data-mp="yoij6xr1bvqe" />
                      </div>
                      <p className="text-white/40 text-[10px] mt-1.5 font-light" data-id="element-7469" data-mp="b1k86flopfzv">
                        Minimum $100. Adjust based on what feels right for your
                        sponsorship.
                      </p>
                    </div>}
                </motion.div>}

              {/* STEP 2: PAYMENT */}
              {step === 'payment' && <motion.div key="payment" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} data-id="element-7470" data-mp="3sjq2at5jkdm5">
                  <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7471" data-mp="1dom666pyttn">
                    Payment Plan
                  </h3>
                  <p className="text-white/55 text-sm font-light mb-7" data-id="element-7472" data-mp="6aykycg00hu5">
                    Complete your{' '}
                    <span className="text-amber-400" data-id="element-7473" data-mp="u09ep9yxlbys">
                      ${form.amount.toLocaleString()}
                    </span>{' '}
                    sponsorship today, or schedule it over installments.
                  </p>

                  <div className="space-y-3" data-id="element-7474" data-mp="145r3o29v95ho">
                    {[{
                  id: 'full' as const,
                  icon: CreditCard,
                  label: 'One-Time Payment',
                  sub: `One charge of $${form.amount.toLocaleString()} today`
                }, {
                  id: 'installments' as const,
                  icon: CalendarClock,
                  label: 'Installment Plan',
                  sub: 'Scheduled monthly payments'
                }].map(opt => {
                  const active = form.paymentPlan === opt.id;
                  return <button key={opt.id} type="button" onClick={() => update('paymentPlan', opt.id)} className={`w-full flex items-start gap-4 border px-5 py-5 text-left transition-colors ${active ? 'border-amber-400/70 bg-amber-400/5' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7475" data-mp="2al1t45j5acyp">
                          <div className={`w-10 h-10 border flex items-center justify-center shrink-0 ${active ? 'border-amber-400 bg-amber-400/15' : 'border-amber-400/30 bg-black/40'}`} data-id="element-7476" data-mp="cwpmmalzitse">
                            <opt.icon className="w-4 h-4 text-amber-400" data-id="element-7477" data-mp="rn571n6ktxpi" />
                          </div>
                          <div className="flex-1" data-id="element-7478" data-mp="vp09iv08uh4v">
                            <p className="font-serif text-white text-base leading-tight" data-id="element-7479" data-mp="vp9z4hhdmma1">
                              {opt.label}
                            </p>
                            <p className="text-white/45 text-xs font-light mt-1" data-id="element-7480" data-mp="2xgwv6xg4l7dd">
                              {opt.sub}
                            </p>
                          </div>
                          {active && <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" data-id="element-7481" data-mp="2uwgwypzfonlb" />}
                        </button>;
                })}
                  </div>

                  {form.paymentPlan === 'installments' && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="mt-5 border border-amber-400/20 bg-amber-950/8 p-5" data-id="element-7482" data-mp="2g7kjueiiskdu">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-3 font-light" data-id="element-7483" data-mp="2zvbgxv950i70">
                        Installment Schedule
                      </label>
                      <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-7484" data-mp="1ofv972fhb0kk">
                        {[3, 6, 12].map(months => {
                    const active = form.installments === months;
                    return <button type="button" key={months} onClick={() => update('installments', months)} className={`px-3 py-3 text-xs tracking-wide border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-bold' : 'border-amber-400/25 text-white/70 hover:border-amber-400/55'}`} data-id="element-7485" data-mp="1byc8fq1nsa0t">
                              {months} months
                            </button>;
                  })}
                      </div>
                      <div className="flex items-baseline justify-between border-t border-amber-400/15 pt-3" data-id="element-7486" data-mp="k7pk08kzw4h4">
                        <span className="text-white/55 text-xs font-light" data-id="element-7487" data-mp="f8uqablhovi8">
                          Per month
                        </span>
                        <span className="font-serif text-amber-400 text-2xl" data-id="element-7488" data-mp="2lvtstygygxkc">
                          ${installmentAmount.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-white/40 text-[10px] mt-2 font-light flex items-center gap-1.5" data-id="element-7489" data-mp="2vsoswlsvn60q">
                        <Lock className="w-2.5 h-2.5" data-id="element-7490" data-mp="2zv3az3oq21qy" />
                        Processed automatically through Stripe each month
                      </p>
                    </motion.div>}
                </motion.div>}

              {/* STEP 3: DETAILS */}
              {step === 'details' && <motion.div key="details" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} className="space-y-6" data-id="element-7491" data-mp="2c3o4cypwwy9w">
                  <div data-id="element-7492" data-mp="2mpmt7okklbuj">
                    <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7493" data-mp="31gvgvs4wwn51">
                      Your Details
                    </h3>
                    <p className="text-white/55 text-sm font-light" data-id="element-7494" data-mp="2wiljdavptag9">
                      Used for confirmation, follow-up, and tax receipt routing
                      if requested.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7495" data-mp="23uy3ua9axa0h">
                    <div data-id="element-7496" data-mp="2htxrpfozpkvl">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7497" data-mp="2hq1laonf9t5o">
                        Full Name *
                      </label>
                      <input className={inputBase} value={form.fullName} onChange={e => update('fullName', e.target.value)} placeholder="Your full name" data-id="element-7498" data-mp="3t5qa7lk8l294" />
                    </div>
                    <div data-id="element-7499" data-mp="5kh2kq13f2wp">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7500" data-mp="3mrayc5urjrsl">
                        Organization / Family
                      </label>
                      <input className={inputBase} value={form.orgName} onChange={e => update('orgName', e.target.value)} placeholder="Optional" data-id="element-7501" data-mp="jaklp84oeghx" />
                    </div>
                    <div data-id="element-7502" data-mp="9iwyjjf5jlm9">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7503" data-mp="ejcaq7np6n2n">
                        Email *
                      </label>
                      <input type="email" className={inputBase} value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@example.com" data-id="element-7504" data-mp="17054nr0txdr7" />
                    </div>
                    <div data-id="element-7505" data-mp="35i8m990t44hr">
                      <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7506" data-mp="35jjyzyc8cml0">
                        Phone *
                      </label>
                      <input type="tel" className={inputBase} value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (___) ___-____" data-id="element-7507" data-mp="219v6eyy7fts" />
                    </div>
                  </div>

                  {/* Tax receipt */}
                  <div className="border border-amber-400/15 bg-amber-950/8 p-5" data-id="element-7508" data-mp="kt2sumoypp7u">
                    <div className="flex items-start gap-3 mb-4" data-id="element-7509" data-mp="g1kp5qvk1aj6">
                      <Receipt className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" data-id="element-7510" data-mp="g09kzpy760rj" />
                      <div data-id="element-7511" data-mp="ocjlhfxx8bgg">
                        <p className="text-white text-sm font-light leading-snug" data-id="element-7512" data-mp="scc0obhtlfus">
                          Would you like a tax receipt issued through{' '}
                          <span className="text-amber-400" data-id="element-7513" data-mp="x9u0ncdv0kqn">
                            Sikh Heritage Museum of Canada
                          </span>
                          ?
                        </p>
                        <p className="text-white/40 text-[11px] mt-1 font-light" data-id="element-7514" data-mp="179ch3iuk9ody">
                          Where applicable. Receipt processing is handled by the
                          museum.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-7515" data-mp="1kzoarm6snv77">
                      {[{
                    v: 'yes',
                    label: 'Yes, request receipt'
                  }, {
                    v: 'no',
                    label: 'No, thank you'
                  }].map(opt => {
                    const active = form.taxReceipt === opt.v;
                    return <button key={opt.v} type="button" onClick={() => update('taxReceipt', opt.v as 'yes' | 'no')} className={`px-3 py-2.5 text-xs tracking-wide border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-bold' : 'border-amber-400/25 text-white/70 hover:border-amber-400/55'}`} data-id="element-7516" data-mp="1f2e7tyy6uusk">
                            {opt.label}
                          </button>;
                  })}
                    </div>
                    {form.taxReceipt === 'yes' && <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="space-y-3 pt-3 border-t border-amber-400/15" data-id="element-7517" data-mp="3uubqv6v4mabs">
                        <div data-id="element-7518" data-mp="25gvedudwg3bz">
                          <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7519" data-mp="1rixa5htalna7">
                            Legal Name for Receipt *
                          </label>
                          <input className={inputBase} value={form.legalName} onChange={e => update('legalName', e.target.value)} placeholder="As it should appear on the receipt" data-id="element-7520" data-mp="1hm2cf14ufqwz" />
                        </div>
                        <div data-id="element-7521" data-mp="23qh413c3ijxt">
                          <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7522" data-mp="23tmqsbx959qd">
                            Mailing Address *
                          </label>
                          <textarea rows={2} className={`${inputBase} resize-none`} value={form.mailingAddress} onChange={e => update('mailingAddress', e.target.value)} placeholder="Street, City, Province/State, Postal Code, Country" data-id="element-7523" data-mp="1ux40l7genzxh" />
                        </div>
                      </motion.div>}
                  </div>

                  {/* Recognition */}
                  <div data-id="element-7524" data-mp="36c13nl9fcpnu">
                    <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-3 font-light" data-id="element-7525" data-mp="31fk660gc4yy3">
                      Recognition Preference *
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-7526" data-mp="3dxwfx0tvmfos">
                      {[{
                    v: 'anonymous' as const,
                    icon: Lock,
                    label: 'Anonymous'
                  }, {
                    v: 'public' as const,
                    icon: UserCircle,
                    label: 'Public Acknowledgment'
                  }, {
                    v: 'family' as const,
                    icon: Heart,
                    label: 'Family Acknowledgment'
                  }, {
                    v: 'organization' as const,
                    icon: Building2,
                    label: 'Business / Organization'
                  }, {
                    v: 'discuss' as const,
                    icon: Phone,
                    label: 'Discuss with Team'
                  }].map(opt => {
                    const active = form.recognitionPref === opt.v;
                    return <button key={opt.v} type="button" onClick={() => update('recognitionPref', opt.v)} className={`flex items-center gap-3 border px-4 py-3 text-left transition-colors ${active ? 'border-amber-400/70 bg-amber-400/5' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-7527" data-mp="15j9hh988zmms">
                            <opt.icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-amber-400' : 'text-amber-400/55'}`} data-id="element-7528" data-mp="mnsuyfnqhf0c" />
                            <span className="text-sm text-white/80 font-light" data-id="element-7529" data-mp="2gyy5m3x8mrp6">
                              {opt.label}
                            </span>
                            {active && <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 ml-auto" data-id="element-7530" data-mp="f47o70sp5d0" />}
                          </button>;
                  })}
                    </div>
                  </div>

                  {/* Notes */}
                  <div data-id="element-7531" data-mp="o1ke959by0kj">
                    <label className="block text-white/70 text-[10px] tracking-[0.3em] uppercase mb-2 font-light" data-id="element-7532" data-mp="o4yk4nh7crwq">
                      Notes / Dedication (Optional)
                    </label>
                    <textarea rows={3} className={`${inputBase} resize-none`} value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Dedicate your sponsorship, share a school preference, or note anything we should know." data-id="element-7533" data-mp="y09xb1cypr2d" />
                  </div>

                  {/* Larger interest */}
                  <label className="flex items-start gap-3 cursor-pointer border border-amber-400/15 bg-black/40 p-4 hover:border-amber-400/35 transition-colors" data-id="element-7534" data-mp="13orszc6miljj">
                    <input type="checkbox" checked={form.largerInterest} onChange={() => update('largerInterest', !form.largerInterest)} className="sr-only" data-id="element-7535" data-mp="17t7wx7wofiw4" />
                    <span className={`mt-0.5 w-4 h-4 border ${form.largerInterest ? 'border-amber-400 bg-amber-400' : 'border-amber-400/40'} flex items-center justify-center shrink-0 transition-colors`} data-id="element-7536" data-mp="1reuf7bnuwxa8">
                      {form.largerInterest && <CheckCircle2 className="w-3 h-3 text-black" data-id="element-7537" data-mp="25en5j5vh1bho" />}
                    </span>
                    <span className="text-white/70 text-sm font-light leading-relaxed" data-id="element-7538" data-mp="2fj3ldz8yaslh">
                      I would like to discuss a larger sponsorship or multiple
                      buses with the team.
                    </span>
                  </label>
                </motion.div>}

              {/* STEP 4: REVIEW */}
              {step === 'review' && <motion.div key="review" initial={{
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -16
            }} transition={{
              duration: 0.3
            }} data-id="element-7539" data-mp="2y8savcu19gtg">
                  <h3 className="font-serif text-2xl text-white mb-2" data-id="element-7540" data-mp="3c8upmq71nu4h">
                    Review & Confirm
                  </h3>
                  <p className="text-white/55 text-sm font-light mb-7" data-id="element-7541" data-mp="2wqjwtuq38vdg">
                    Confirm your sponsorship details before secure Stripe
                    checkout.
                  </p>

                  <div className="space-y-4 mb-7" data-id="element-7542" data-mp="2nqwvf8xczpke">
                    {[{
                  label: 'Sponsorship Type',
                  value: form.sponsorshipType === 'full' ? 'Full Bus Sponsor' : form.sponsorshipType === 'shared' ? 'Shared Bus Sponsor' : 'Flexible Supporter'
                }, {
                  label: 'Total Amount',
                  value: `$${form.amount.toLocaleString()}`,
                  highlight: true
                }, {
                  label: 'Payment Plan',
                  value: form.paymentPlan === 'full' ? 'One-time payment' : `${form.installments} monthly installments of $${installmentAmount.toFixed(2)}`
                }, {
                  label: 'Sponsor',
                  value: `${form.fullName}${form.orgName ? ` · ${form.orgName}` : ''}`
                }, {
                  label: 'Email',
                  value: form.email
                }, {
                  label: 'Tax Receipt',
                  value: form.taxReceipt === 'yes' ? `Requested · ${form.legalName}` : 'Not requested'
                }, {
                  label: 'Recognition',
                  value: form.recognitionPref === 'anonymous' ? 'Anonymous' : form.recognitionPref === 'public' ? 'Public Acknowledgment' : form.recognitionPref === 'family' ? 'Family Acknowledgment' : form.recognitionPref === 'organization' ? 'Business / Organization' : 'Discuss with team'
                }].map(row => <div key={row.label} className="flex items-start justify-between gap-4 pb-3 border-b border-amber-400/10" data-id="element-7543" data-mp="2ijdf17n9j0b4">
                        <span className="text-amber-400/60 text-[10px] tracking-[0.3em] uppercase font-light shrink-0" data-id="element-7544" data-mp="2sa84ekvt2g0s">
                          {row.label}
                        </span>
                        <span className={`text-right text-sm font-light ${row.highlight ? 'text-amber-400 font-serif text-lg' : 'text-white/80'}`} data-id="element-7545" data-mp="1zr1hcy7k0n7b">
                          {row.value}
                        </span>
                      </div>)}
                  </div>

                  {/* Stripe panel */}
                  <div className="border border-amber-400/30 bg-amber-950/8 p-5 flex items-center gap-4" data-id="element-7546" data-mp="2wnkm80bq1d1">
                    <div className="w-12 h-12 border border-amber-400/40 bg-black/60 flex items-center justify-center shrink-0" data-id="element-7547" data-mp="3u4ta3sftdcd7">
                      <Lock className="w-5 h-5 text-amber-400" data-id="element-7548" data-mp="3pb6wny1tt4gc" />
                    </div>
                    <div className="flex-1" data-id="element-7549" data-mp="3lby2is2vj4k9">
                      <p className="text-white text-sm font-light leading-snug" data-id="element-7550" data-mp="3gd4yrrde2rxa">
                        Secure payment processed by{' '}
                        <span className="text-amber-400 font-medium" data-id="element-7551" data-mp="3bd0jg4o7w38n">
                          Stripe
                        </span>
                      </p>
                      <p className="text-white/45 text-xs mt-0.5 font-light" data-id="element-7552" data-mp="z2pajafq6dqs">
                        You&rsquo;ll be redirected to Stripe&rsquo;s secure
                        checkout to complete your sponsorship.
                      </p>
                    </div>
                  </div>
                </motion.div>}

              {/* PROCESSING */}
              {step === 'processing' && <motion.div key="processing" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="py-14 flex flex-col items-center text-center" data-id="element-7553" data-mp="fezlvc334tls">
                  <Loader2 className="w-10 h-10 text-amber-400 animate-spin mb-5" data-id="element-7554" data-mp="befpiaqc5u0p" />
                  <p className="font-serif text-xl text-white mb-2" data-id="element-7555" data-mp="3smt15g1r2rxu">
                    Connecting to Stripe...
                  </p>
                  <p className="text-white/45 text-sm font-light" data-id="element-7556" data-mp="1hkuw2x6odrp">
                    Securing your sponsorship commitment
                  </p>
                </motion.div>}

              {/* CONFIRMED */}
              {step === 'confirmed' && <motion.div key="confirmed" initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center py-6" data-id="element-7557" data-mp="3nya0fmy0d4s3">
                  <motion.div initial={{
                scale: 0.6,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 280,
                damping: 20
              }} className="inline-flex w-16 h-16 border-2 border-amber-400 bg-amber-400/10 items-center justify-center mb-7 shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-7558" data-mp="3suio5oq9u6av">
                    <CheckCircle2 className="w-8 h-8 text-amber-400" data-id="element-7559" data-mp="111dafzl8fu9p" />
                  </motion.div>
                  <Eyebrow data-id="element-7560" data-mp="wyqepnlfxp78">Sponsorship Confirmed</Eyebrow>
                  <h3 className="font-serif text-3xl sm:text-4xl text-white mt-4 mb-4 leading-tight" data-id="element-7561" data-mp="wx5htrbnlu67">
                    Thank You,{' '}
                    <span className="text-amber-400" data-id="element-7562" data-mp="4b4acd4e2gjb">
                      {form.fullName.split(' ')[0]}
                    </span>
                  </h3>
                  <p className="text-white/65 text-base font-light leading-relaxed max-w-md mx-auto mb-6" data-id="element-7563" data-mp="i3ahxath7f75">
                    Your sponsorship of{' '}
                    <span className="text-amber-400 font-medium" data-id="element-7564" data-mp="3lqarqw8s389h">
                      ${form.amount.toLocaleString()}
                    </span>{' '}
                    has been received. A confirmation has been sent to{' '}
                    <span className="text-amber-400" data-id="element-7565" data-mp="37jzcx50ubpv2">{form.email}</span>.
                  </p>
                  {form.taxReceipt === 'yes' && <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10 text-amber-400 text-[10px] tracking-[0.25em] uppercase font-bold mb-3" data-id="element-7566" data-mp="2xne3m7hsfslf">
                      <Receipt className="w-3 h-3" data-id="element-7567" data-mp="3qc1vwn0yfi8h" />
                      Tax Receipt Routing Initiated
                    </div>}
                </motion.div>}
            </AnimatePresence>

            {/* Nav buttons */}
            {step !== 'processing' && step !== 'confirmed' && <div className="flex items-center justify-between gap-4 pt-7 mt-7 border-t border-amber-400/15" data-id="element-7568" data-mp="3lcfhia9e3vy3">
                <button type="button" onClick={handleBack} disabled={step === 'type'} className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors ${step === 'type' ? 'opacity-30 cursor-not-allowed text-white/50' : 'text-amber-400 border border-amber-400/30 hover:bg-amber-400/10'}`} data-id="element-7569" data-mp="24yfj53q70ecy">
                  <ArrowLeft className="w-3 h-3" data-id="element-7570" data-mp="6nf6klmuah44" />
                  Back
                </button>
                <button type="button" onClick={handleNext} disabled={!canAdvance()} className={`inline-flex items-center gap-2 px-6 py-3.5 text-[10px] tracking-[0.3em] uppercase font-bold transition-colors ${canAdvance() ? 'bg-amber-400 text-black hover:bg-amber-300 shadow-[0_0_30px_rgba(212,175,55,0.25)]' : 'bg-amber-400/15 text-black/40 cursor-not-allowed'}`} data-id="element-7571" data-mp="1pwtqk5pbhqu">
                  {step === 'review' ? <>
                      <Lock className="w-3.5 h-3.5" data-id="element-7572" data-mp="1i22g51vluhk1" />
                      Confirm &amp; Pay
                    </> : <>
                      Continue
                      <ArrowRight className="w-3.5 h-3.5" data-id="element-7573" data-mp="1kn0ewi5zs1pt" />
                    </>}
                </button>
              </div>}
          </div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// WHAT HAPPENS NEXT
// =====================================================================
const WhatHappensNext = () => {
  const steps = [{
    n: '01',
    icon: CheckCircle2,
    title: 'Payment Confirmed',
    body: 'Stripe processes your payment. You receive an instant confirmation email.'
  }, {
    n: '02',
    icon: FileText,
    title: 'Sponsor Details Reviewed',
    body: 'Our team reviews your sponsorship details and recognition preference.'
  }, {
    n: '03',
    icon: Receipt,
    title: 'Tax Receipt Routed',
    body: 'If requested, your details are routed to Sikh Heritage Museum of Canada for issuance.'
  }, {
    n: '04',
    icon: Award,
    title: 'Impact &amp; Recognition',
    body: 'You receive updates on student access and your chosen acknowledgment goes live.'
  }];
  return <section className="relative py-24 md:py-28 bg-[#080808] border-t border-amber-400/10" data-id="element-7574" data-mp="2znij6kgtd0ve">
      <div className="container mx-auto px-4" data-id="element-7575" data-mp="2um2ywumgmye2">
        <SectionHeading eyebrow="After Payment" title="What Happens" highlight="Next" intro="A clear four-step follow-up after your sponsorship is confirmed." data-id="element-7576" data-mp="38mm1vnc2je9d" />
        <div className="max-w-6xl mx-auto mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7577" data-mp="3ic5liv7lvc5a">
          {steps.map((s, i) => <motion.div key={s.n} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="relative border border-amber-400/15 hover:border-amber-400/40 transition-colors bg-gradient-to-b from-amber-950/10 to-transparent p-6" data-id="element-7578" data-mp="3den9ux27eitl" data-mpi={i}>
              <span className="absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-7579" data-mp="5rrp5sb2quv4" />
              <div className="flex items-center justify-between mb-4" data-id="element-7580" data-mp="3n7818cbgehxb">
                <p className="font-serif text-amber-400/50 text-3xl leading-none" data-id="element-7581" data-mp="3s1lzqnlex567">
                  {s.n}
                </p>
                <div className="w-9 h-9 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-7582" data-mp="3e6jodkxsqnm0">
                  <s.icon className="w-4 h-4 text-amber-400" data-id="element-7583" data-mp="34agj467ne8uu" />
                </div>
              </div>
              <h4 className="font-serif text-lg text-white leading-tight mb-2" dangerouslySetInnerHTML={{
            __html: s.title
          }} data-id="element-7584" data-mp="399jo5qnmof2t" />
              <p className="text-white/55 text-sm font-light leading-relaxed" dangerouslySetInnerHTML={{
            __html: s.body
          }} data-id="element-7585" data-mp="2lehq7asiqs26" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// SPONSOR STATUS DASHBOARD (post-confirmation only)
// =====================================================================
const SponsorStatus = ({
  data
}: {
  data: FormState;
}) => {
  const paid = data.paymentPlan === 'full' ? data.amount : data.amount / data.installments;
  const remaining = data.paymentPlan === 'full' ? 0 : data.amount - paid;
  const progress = data.paymentPlan === 'full' ? 100 : 1 / data.installments * 100;
  return <section className="relative py-20 bg-black border-t border-amber-400/15" data-id="element-7586" data-mp="1iquz04ee2l5b">
      <div className="container mx-auto px-4" data-id="element-7587" data-mp="3uezw7un2skjl">
        <SectionHeading eyebrow="Your Sponsorship" title="Live" highlight="Status" intro="A snapshot of your active sponsorship and what comes next." data-id="element-7588" data-mp="3ug1as93avp62" />
        <div className="max-w-4xl mx-auto mt-12 relative border border-amber-400/40 bg-gradient-to-br from-amber-950/15 via-black to-black p-7 sm:p-9" data-id="element-7589" data-mp="85x9fo03f3o8">
          <CornerOrnaments data-id="element-7590" data-mp="hxl5k27lh8w1" />
          <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-7591" data-mp="hw9stcw68qss" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8" data-id="element-7592" data-mp="lx3hi8hfu9mq">
            <div data-id="element-7593" data-mp="qz13hah83ijd">
              <Eyebrow data-id="element-7594" data-mp="qyj4i636eq7r">Pledged</Eyebrow>
              <p className="font-serif text-3xl text-white mt-2" data-id="element-7595" data-mp="vwuau6u9iil8">
                ${data.amount.toLocaleString()}
              </p>
            </div>
            <div data-id="element-7596" data-mp="2sqxg6nf4myfq">
              <Eyebrow data-id="element-7597" data-mp="2srgza7gijsvc">Paid</Eyebrow>
              <p className="font-serif text-3xl text-amber-400 mt-2" data-id="element-7598" data-mp="2xo7hb0bvoyje">
                ${paid.toLocaleString()}
              </p>
            </div>
            <div data-id="element-7599" data-mp="13f0w1zwtysy2">
              <Eyebrow data-id="element-7600" data-mp="13einq4ww8o9s">Remaining</Eyebrow>
              <p className="font-serif text-3xl text-white/60 mt-2" data-id="element-7601" data-mp="yi1z79ls87w6">
                ${remaining.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="mb-2 flex items-center justify-between text-[10px] tracking-[0.25em] uppercase" data-id="element-7602" data-mp="1d899v35jsmmb">
            <span className="text-amber-400/70" data-id="element-7603" data-mp="khrbugjolbfr">Progress</span>
            <span className="text-white/50" data-id="element-7604" data-mp="ki9cdpddmbth">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-amber-400/10 overflow-hidden mb-8" data-id="element-7605" data-mp="fjy2jen301b9">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.8
          }} className="h-full bg-gradient-to-r from-amber-400/60 via-amber-400 to-amber-300" data-id="element-7606" data-mp="uggkdipotjjc" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7607" data-mp="28fibk0r0kjs2">
            {[{
            label: 'Plan',
            value: data.paymentPlan === 'full' ? 'One-Time Payment' : `${data.installments} Installments`
          }, {
            label: 'Tax Receipt',
            value: data.taxReceipt === 'yes' ? 'Routing to Sikh Heritage Museum' : 'Not requested'
          }, {
            label: 'Recognition',
            value: data.recognitionPref === 'anonymous' ? 'Anonymous' : data.recognitionPref === 'public' ? 'Public' : data.recognitionPref === 'family' ? 'Family' : data.recognitionPref === 'organization' ? 'Organization' : 'Discuss with team'
          }, {
            label: 'Impact',
            value: data.sponsorshipType === 'full' ? '~50 students access' : data.sponsorshipType === 'shared' ? 'Shared bus support' : 'Flexible support'
          }].map(row => <div key={row.label} className="border border-amber-400/15 bg-black/40 px-4 py-3 flex items-center justify-between" data-id="element-7608" data-mp="1946wwio56s4">
                <span className="text-amber-400/60 text-[10px] tracking-[0.25em] uppercase" data-id="element-7609" data-mp="a9addyfy96hs">
                  {row.label}
                </span>
                <span className="text-white/75 text-xs font-light text-right" data-id="element-7610" data-mp="f9oq3tshx4m5">
                  {row.value}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// IMPACT BLOCK
// =====================================================================
const ImpactBlock = () => <section className="relative py-24 md:py-28 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-7611" data-mp="36704fpv6z8td">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.05),_transparent_70%)] pointer-events-none" data-id="element-7612" data-mp="31dosxbffq3v4" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-7613" data-mp="2sejvk8fm0gkj">
      <div className="max-w-4xl mx-auto" data-id="element-7614" data-mp="2scqpovctm0dl">
        <SectionHeading eyebrow="What Sponsorship Makes Possible" title="Access That" highlight="Otherwise Wouldn't Exist" data-id="element-7615" data-mp="2nf7b1uc7nll4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12" data-id="element-7616" data-mp="2iiqfwg6lg6yj">
          {[{
          icon: GraduationCap,
          body: 'Brings students into an immersive Sikh history experience.'
        }, {
          icon: Building2,
          body: 'Supports school participation across districts.'
        }, {
          icon: Users,
          body: 'Creates access for young people who may not otherwise attend.'
        }, {
          icon: Heart,
          body: 'Connects heritage, identity, and meaningful learning.'
        }].map((it, i) => <motion.div key={it.body} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="border border-amber-400/15 bg-gradient-to-r from-amber-950/10 to-transparent p-5 flex items-start gap-4" data-id="element-7617" data-mp="2183bard65um5" data-mpi={i}>
              <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center shrink-0" data-id="element-7618" data-mp="18j5rukx81rrt">
                <it.icon className="w-4 h-4 text-amber-400" data-id="element-7619" data-mp="10y3n48jjyva2" />
              </div>
              <p className="text-white/75 text-sm sm:text-base font-light leading-relaxed pt-1.5" data-id="element-7620" data-mp="w0jz4gx1z44x">
                {it.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>
  </section>;
// =====================================================================
// FAQ
// =====================================================================
const FAQ = () => {
  const items = [{
    q: 'What does a bus sponsorship cover?',
    a: 'Each sponsored bus covers round-trip transportation, experience access tickets for approximately 50 students, and school-side coordination.'
  }, {
    q: 'Can I sponsor part of a bus?',
    a: 'Yes. Shared Bus Sponsors pool contributions toward a single bus. We track progress and acknowledge all contributors.'
  }, {
    q: 'Can I pay in installments?',
    a: 'Yes. Installments are processed through Stripe over 3, 6, or 12 months. Your full commitment is confirmed at signup.'
  }, {
    q: 'How do Stripe payments work?',
    a: 'You\u2019re redirected to Stripe\u2019s secure checkout. We never store your card details. You\u2019ll receive a confirmation email from Stripe and from our team.'
  }, {
    q: 'Who issues the tax receipt?',
    a: 'Tax receipts, where applicable, are issued by Sikh Heritage Museum of Canada \u2014 not directly through Sikh Kingdoms.'
  }, {
    q: 'When is the tax receipt provided?',
    a: 'After your details are reviewed and routed to Sikh Heritage Museum of Canada. Timing is subject to the museum\u2019s processing workflow.'
  }, {
    q: 'Can my family or business be recognized?',
    a: 'Yes. You can choose anonymous, family, business, or public acknowledgment \u2014 or discuss custom recognition with the team.'
  }, {
    q: 'Can I sponsor more than one bus?',
    a: 'Absolutely. Check the "discuss a larger sponsorship" option in checkout and our team will reach out to coordinate.'
  }, {
    q: 'Can I speak with someone before paying?',
    a: 'Yes. Use the "Speak With Our Team" button below to arrange a conversation before committing.'
  }];
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return <section className="relative py-24 md:py-28 bg-[#080808] border-t border-amber-400/10" data-id="element-7621" data-mp="19ytb1xzacxjk">
      <div className="container mx-auto px-4" data-id="element-7622" data-mp="27nva7s8bhr32">
        <SectionHeading eyebrow="FAQ" title="Common" highlight="Questions" intro="Everything you might want to know about sponsorship, payment, and tax receipts." data-id="element-7623" data-mp="22p1xw34ro64h" />
        <div className="max-w-3xl mx-auto mt-14 space-y-2.5" data-id="element-7624" data-mp="1tpn690cv5pie">
          {items.map((it, i) => {
          const open = openIdx === i;
          return <div key={it.q} className={`relative border bg-gradient-to-r from-amber-950/8 to-transparent transition-colors ${open ? 'border-amber-400/40' : 'border-amber-400/15 hover:border-amber-400/30'}`} data-id="element-7625" data-mp="m8z2vtpwvx5n" data-mpi={i}>
                <button type="button" onClick={() => setOpenIdx(open ? null : i)} className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left" data-id="element-7626" data-mp="oonjz8ye6228">
                  <span className="font-serif text-white text-base leading-tight pr-4" data-id="element-7627" data-mp="etlxylzexg3v">
                    {it.q}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-amber-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-7628" data-mp="5rcn1v08fkpy" />
                </button>
                <AnimatePresence data-id="element-7629" data-mp="3s65vbqddhr7e">
                  {open && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-7630" data-mp="3s4ckrzzn2mu4">
                      <p className="px-5 pb-5 text-white/65 text-sm font-light leading-relaxed" data-id="element-7631" data-mp="38isvgc905ku4">
                        {it.a}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
};
// =====================================================================
// FINAL CTA
// =====================================================================
const FinalCTA = ({
  onSponsorClick
}: {
  onSponsorClick: () => void;
}) => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-7632" data-mp="55892x3a5hgr">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.1),_transparent_60%)] pointer-events-none" data-id="element-7633" data-mp="6575liljrd0" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-7634" data-mp="17jm6o3tr0tic">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-3xl mx-auto text-center" data-id="element-7635" data-mp="1bi38q8ybbdsw">
        <Eyebrow data-id="element-7636" data-mp="nu29jqn0x9q5">Sponsor Student Access</Eyebrow>
        <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-7637" data-mp="nvdc5n4qgbfo">
          Bring a bus.{' '}
          <span className="text-amber-400 italic" data-id="element-7638" data-mp="xr7zuwntcwpb">Open a door.</span>
        </h2>
        <div className="w-20 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-7639" data-mp="12ig83pdeqc0w" />
        <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed mb-10" data-id="element-7640" data-mp="12jrcnreofnt2">
          Support meaningful access to Sikh history through a bus sponsorship,
          shared contribution, or structured installment plan.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7641" data-mp="2icrafyxqis9">
          <button onClick={onSponsorClick} className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-7642" data-mp="3toxoyijhwoy9">
            <Bus className="w-4 h-4" data-id="element-7643" data-mp="3fp65w3e4gide" />
            Sponsor a Bus
          </button>
          <a href="mailto:court@lahoretreasury.com?subject=Bus%20Sponsorship%20Inquiry" className="group inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-7644" data-mp="3fr91d2y1n3pi">
            <Phone className="w-3.5 h-3.5" data-id="element-7645" data-mp="2w042af2ocypf" />
            Speak With Our Team
          </a>
        </div>
        <p className="mt-8 text-white/35 text-xs font-light flex items-center justify-center gap-2" data-id="element-7646" data-mp="2s0mx7mr5u3ks">
          <Mail className="w-3 h-3" data-id="element-7647" data-mp="1xp07nxat63nu" />
          court@lahoretreasury.com
        </p>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// PAGE
// =====================================================================
export const SponsorABusPage = () => {
  const [preSelectedType, setPreSelectedType] = useState<SponsorshipType>('');
  const [confirmedData, setConfirmedData] = useState<FormState | null>(null);
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const handleChooseType = (t: SponsorshipType) => {
    setPreSelectedType(t);
    setTimeout(() => scrollTo('checkout'), 100);
  };
  return <LanguageProvider data-id="element-7648" data-mp="2z5zhf97426eq">
      <div className="bg-black min-h-screen text-white selection:bg-amber-400/30" data-id="element-7649" data-mp="2z691kg5h5jci">
        <ScrollProgress data-id="element-7650" data-mp="2udfxzvlqsh20" />
        <Navbar data-id="element-7651" data-mp="2uc4snrcnf18s" />
        <main data-id="element-7652" data-mp="gvl9fnkofmcv">
          <Hero scrollToCheckout={() => scrollTo('checkout')} scrollToOptions={() => scrollTo('sponsorship-options')} scrollToTax={() => scrollTo('tax-receipt')} data-id="element-7653" data-mp="gw4pszuoi2os" />
          <WhatItFunds data-id="element-7654" data-mp="pw061j33cnvo" />
          <SponsorshipOptions onChoose={handleChooseType} data-id="element-7655" data-mp="pvhyhqazan8g" />
          <PaymentOptions data-id="element-7656" data-mp="uvvzioe5wwwz" />
          <TaxReceipt data-id="element-7657" data-mp="uwflcg6xo5ga" />
          <Checkout initialType={preSelectedType} onConfirmed={s => setConfirmedData(s)} data-id="element-7658" data-mp="uwnxu6z598qy" />
          {confirmedData && <SponsorStatus data={confirmedData} data-id="element-7659" data-mp="14t0oevze76hh" />}
          <WhatHappensNext data-id="element-7660" data-mp="14rpl0x9v00qw" />
          <ImpactBlock data-id="element-7661" data-mp="19k8y7t4mwtv6" />
          <FAQ data-id="element-7662" data-mp="19jqvcdngvhea" />
          <FinalCTA onSponsorClick={() => scrollTo('checkout')} data-id="element-7663" data-mp="19lbpi25vcybm" />
        </main>
        <Footer data-id="element-7664" data-mp="1dmdnkfo1v4c0" />
      </div>
    </LanguageProvider>;
};